import React, { useEffect, useRef } from "react";
import { Placeholder } from "@sitecore-jss/sitecore-jss-react";
import { Section, Container } from "components/shared";
import styled from "styled-components";
import templateIds from "utils/templateIds";
import { useSitecoreContext } from "stores/sitecoreContext";
import pageTypes from "utils/pageTypes";
import { useLocation } from "react-router-dom";
import { scrollToSection } from "utils/scrollTo";
import cx from "classnames";
import useDynamicsForm from "utils/hooks/useDynamicsForm";

const StyledSection = styled(Section)`
  margin-top: 3.125rem;
  padding-bottom: 4.875rem;
  ${({ withoutMargin }) => (withoutMargin ? `` : `margin-bottom: 1.875rem;`)}
`;

const hasSection = (rendering, placeholder, field) => {
  try {
    return !!rendering.placeholders[placeholder][0].fields[field].value;
  } catch (err) {
    return false;
  }
};

const hasContactSection = (rendering) => {
  return (
    hasSection(rendering, "row1xcolumn2andcolumn3", "phoneNumberCall") ||
    hasSection(rendering, "row1xcolumn2andcolumn3", "zipcode") ||
    hasSection(rendering, "row1xcolumn2andcolumn3", "firstAddress") ||
    hasSection(rendering, "row2xcolumn2", "email") ||
    hasSection(rendering, "row2xcolumn3", "email")
  );
};

const GetInTouchContainer = ({ rendering }) => {
  const { templateId = "" } = useSitecoreContext() || {};
  const currentPageType =
    rendering.placeholders.row1androw2xcolumn1[0] &&
    rendering.placeholders.row1androw2xcolumn1[0].fields &&
    rendering.placeholders.row1androw2xcolumn1[0].fields.currentPageType &&
    rendering.placeholders.row1androw2xcolumn1[0].fields.currentPageType.value;
  const isCustomCarePage =
    rendering.placeholders["column-form"][0] &&
    rendering.placeholders["column-form"][0].fields &&
    rendering.placeholders["column-form"][0].fields.currentPageType &&
    rendering.placeholders["column-form"][0].fields.currentPageType.value ===
      pageTypes.SupportPage;

  const dynamicsScriptsCodeRef = useRef();
  const {
    dataFormBlockId,
    formDataScriptSrc,
    formDataTrackingScriptSrc,
    trackingDiv,
    formDataDiv,
    dynamicsDiv,
  } = useDynamicsForm();

  const location = useLocation();
  const hash = location.hash;
  useEffect(() => {
    if (hash && (hash === "warrantyrequest" || hash === "contact"))
      setTimeout(() => {
        scrollToSection(hash.substring(1), 80);
      }, 1000);
  }, [hash]);

  const externalFormRef = useRef();
  useEffect(() => {
    setTimeout(() => {
      if (externalFormRef.current) {
        const externalForm =
          externalFormRef.current &&
          externalFormRef.current.children &&
          externalFormRef.current.children[0];
        if (externalForm) {
          //externalForm.setAttribute("novalidate", true);
          const childNodesForm = externalForm.children;
          if (childNodesForm && childNodesForm.length > 0) {
            for (let i = 0; i < childNodesForm.length; i++) {
              if (childNodesForm[i].tagName === "STYLE") {
                childNodesForm[i].innerHTML = "";
              }
            }
          }
        }
      }
    }, 3000);
  }, [externalFormRef]);

  useEffect(() => {
    const parsedTRHTML = document
      .createRange()
      .createContextualFragment(trackingDiv);
    const parsedFCHTMLChildrens = document
      .createRange()
      .createContextualFragment(formDataDiv).children;

    if (
      hasExternalForm &&
      !enableDynaMXFormAPI &&
      dynamicsScriptsCodeRef &&
      trackingDiv &&
      formDataDiv &&
      dynamicsDiv
    ) {
      dynamicsScriptsCodeRef.current.appendChild(parsedTRHTML);

      for (let i = 0; i < parsedFCHTMLChildrens.length; i++) {
        if (parsedFCHTMLChildrens[i].nodeName === "DIV")
          dynamicsScriptsCodeRef.current.appendChild(parsedFCHTMLChildrens[i]);
      }
    }
    setTimeout(() => {
      if (hasExternalForm && !enableDynaMXFormAPI) {
        console.log("trying to load scripts on getIntouchContainer");
        let trackingScript = document.createElement("script");
        trackingScript.src = formDataTrackingScriptSrc;
        trackingScript.onload = () => {
          console.log("getIntouchContainer trackingScript loaded");
        };
        trackingScript.onerror = (e) => {
          console.log("getIntouchContainer trackingScript error", e);
        };
        document.head.appendChild(trackingScript);
        let formDataScript = document.createElement("script");
        formDataScript.src = formDataScriptSrc;
        formDataScript.onload = () => {
          console.log("getIntouchContainer formDataScript loaded");
        };
        formDataScript.onerror = (e) => {
          console.log("getIntouchContainer formDataScript error", e);
        };
        document.head.appendChild(formDataScript);
      }
      if (hasExternalForm && enableDynaMXFormAPI) {
        console.log("trying to load FormCapture script on getIntouchContainer");
        let formCaptureScript = document.createElement("script");
        formCaptureScript.src =
          "https://cxppusa1formui01cdnsa01-endpoint.azureedge.net/can/FormCapture/FormCapture.bundle.js";
        formCaptureScript.onload = () => {
          console.log("getIntouchContainer FormCapture script loaded");
        };
        formCaptureScript.onerror = (e) => {
          console.log("getIntouchContainer FormCapture script error", e);
        };
        document.head.appendChild(formCaptureScript);
      }
    }, 1000);
  }, [hasExternalForm]);
  const hasExternalForm =
    rendering.placeholders["column-form"][0] &&
    rendering.placeholders["column-form"][0].fields &&
    rendering.placeholders["column-form"][0].fields.enableDynamicForm &&
    rendering.placeholders["column-form"][0].fields.enableDynamicForm.value;
  const dynamicsFormFields =
    rendering.placeholders["column-form"][0] &&
    rendering.placeholders["column-form"][0].fields &&
    rendering.placeholders["column-form"][0].fields.dynamicsFormFields &&
    rendering.placeholders["column-form"][0].fields.dynamicsFormFields.value;
  const currentPageTypeForm =
    rendering.placeholders["column-form"][0] &&
    rendering.placeholders["column-form"][0].fields &&
    rendering.placeholders["column-form"][0].fields.currentPageType &&
    rendering.placeholders["column-form"][0].fields.currentPageType.value;
  const enableDynaMXFormAPI =
    rendering.placeholders["column-form"][0] &&
    rendering.placeholders["column-form"][0].fields &&
    rendering.placeholders["column-form"][0].fields.enableDynaMXFormAPI &&
    rendering.placeholders["column-form"][0].fields.enableDynaMXFormAPI.value;

  return (
    <>
      <StyledSection
        className="bg-light-blue"
        withoutMargin={
          templateId === templateIds.CondoPlan ||
          templateId === templateIds.MpcPage ||
          templateId === templateIds.CommunityPage ||
          templateId === templateIds.CondoPage||
          templateId === templateIds.QMIPage ||
          templateId === templateIds.PlanPage
        }
        id={isCustomCarePage ? "warrantyrequest" : "contact"}
        tabIndex={-1}
      >
        <Container id={"sales-center-contact"} tabIndex="-1" className="w-full">
          <div className="px-0 lg:px-16 h-full flex flex-col justify-center overflow-visible">
            <div className="flex w-full mb-10 md:mb-0">
              <Placeholder name="row1androw2xcolumn1" rendering={rendering} />
            </div>
            {currentPageType === pageTypes.MarketPage || isCustomCarePage ? (
              <div className="flex flex-col w-full mb-10 md:mb-0">
                <Placeholder
                  name="column-form"
                  rendering={rendering}
                  hasExternalForm={hasExternalForm && !enableDynaMXFormAPI}
                  enableDynaMXFormAPI={hasExternalForm && enableDynaMXFormAPI}
                  hasNoSectionsLeft={!hasContactSection(rendering)}
                  dynamicsFormFields={dynamicsFormFields}
                  currentPageType={currentPageTypeForm}
                />
              </div>
            ) : (
              <div className="flex flex-col md:flex-row w-full mb-10 md:mb-0">
                {hasContactSection(rendering) && (
                  <div className="flex flex-col w-full lg:w-5/12 mb-10 md:mb-0 md:mr-10">
                    {(hasSection(
                      rendering,
                      "row1xcolumn2andcolumn3",
                      "phoneNumberCall"
                    ) ||
                      hasSection(
                        rendering,
                        "row1xcolumn2andcolumn3",
                        "zipcode"
                      ) ||
                      hasSection(
                        rendering,
                        "row1xcolumn2andcolumn3",
                        "firstAddress"
                      )) && (
                      <Placeholder
                        name="row1xcolumn2andcolumn3"
                        rendering={rendering}
                      />
                    )}
                    {hasSection(rendering, "row2xcolumn2", "email") && (
                      <div className="flex w-full mb-10 md:mb-12">
                        <Placeholder
                          name="row2xcolumn2"
                          rendering={rendering}
                        />
                      </div>
                    )}
                  </div>
                )}
                <div
                  className={cx(
                    "flex flex-col w-full justify-between flex-wrap ",
                    {
                      "lg:pl-4 lg:w-7/12 ": hasContactSection(rendering),
                    }
                  )}
                >
                  <div className="flex flex-col w-full">
                    <Placeholder
                      name="column-form"
                      rendering={rendering}
                      hasExternalForm={hasExternalForm && !enableDynaMXFormAPI}
                      enableDynaMXFormAPI={
                        hasExternalForm && enableDynaMXFormAPI
                      }
                      hasNoSectionsLeft={!hasContactSection(rendering)}
                      dynamicsFormFields={dynamicsFormFields}
                      currentPageType={currentPageTypeForm}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </Container>
      </StyledSection>
      {hasExternalForm && !enableDynaMXFormAPI && (
        <>
          <div
            data-form-block-id={dataFormBlockId} //"fac3b03a-ff27-ee11-9965-002248ae0846"
            className="hidden"
            ref={externalFormRef}
          ></div>
          <div ref={dynamicsScriptsCodeRef}></div>
          {/*
          <Helmet>
            <div
              className="d365-mkt-config"
              style="display:none"
              data-website-id="q-UbDFtxMBIM6cA-NrjzDWBvPkqUkJxwrPYb_782Se8"
              data-hostname="a938dd2966244ca8bbc39fa7ae339641.svc.dynamics.com"
            ></div>
          </Helmet> */}
        </>
      )}
    </>
  );
};
export default GetInTouchContainer;
